import React, { useEffect, useState, useRef } from 'react';
import { useTheme, T } from '../context/ThemeContext';

const API = '/api';
const token = () => localStorage.getItem('semsim_token');

const STATUS_COLORS = {
  new: '#ef4444',
  acknowledged: '#f59e0b',
  in_treatment: '#3b82f6',
  resolved: '#22c55e',
};
const STATUS_HE = { new:'חדש', acknowledged:'בטיפול', in_treatment:'בטיפול מתקדם', resolved:'סגור' };
const STATUS_EN = { new:'New', acknowledged:'Acknowledged', in_treatment:'In Treatment', resolved:'Resolved' };

export default function ReportsPage({ user, onBack }) {
  const { dark, setDark, lang, setLang } = useTheme();
  const t = T[lang];
  const rtl = lang === 'he';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ from:'', to:'', municipality:'', site_id:'', status:'' });
  const [sites, setSites] = useState([]);
  const [munis, setMunis] = useState([]);
  const printRef = useRef();

  const c = dark ? {
    bg:'#060d1a', topbar:'#0a1628', card:'#0f1f38', border:'#1e3a5f',
    text:'#fff', sub:'#94a3b8', input:'#0a1628', th:'#0a1628',
    tr1:'#0f1f38', tr2:'#0a1628', badge:'#1e3a5f',
  } : {
    bg:'#f0f4f8', topbar:'#1e40af', card:'#ffffff', border:'#bfdbfe',
    text:'#1e293b', sub:'#475569', input:'#ffffff', th:'#eff6ff',
    tr1:'#ffffff', tr2:'#f8fafc', badge:'#e2e8f0',
  };

  useEffect(() => {
    fetch(`${API}/municipalities`, { headers:{ Authorization:`Bearer ${token()}` } })
      .then(r=>r.json()).then(d=>setMunis(Array.isArray(d)?d:[])).catch(()=>{});
    fetch(`${API}/sites`, { headers:{ Authorization:`Bearer ${token()}` } })
      .then(r=>r.json()).then(d=>setSites(Array.isArray(d)?d:[])).catch(()=>{});
    loadData();
  }, []);

  const loadData = async (f = filters) => {
    setLoading(true);
    try {
      const q = new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([,v])=>v)));
      const r = await fetch(`${API}/reports?${q}`, { headers:{ Authorization:`Bearer ${token()}` } });
      setData(await r.json());
    } catch {} finally { setLoading(false); }
  };

  const setF = (k,v) => setFilters(p=>({...p,[k]:v}));

  // ── גרפים SVG ──
  const PieChart = ({ data: d }) => {
    if (!d?.length) return null;
    const total = d.reduce((s,r)=>s+parseInt(r.count),0);
    if (!total) return null;
    let angle = -Math.PI/2;
    const slices = d.map(r => {
      const pct = parseInt(r.count)/total;
      const a1 = angle, a2 = angle + pct*2*Math.PI;
      angle = a2;
      const x1=Math.cos(a1)*80+100, y1=Math.sin(a1)*80+100;
      const x2=Math.cos(a2)*80+100, y2=Math.sin(a2)*80+100;
      const large = pct>0.5?1:0;
      return { path:`M100,100 L${x1},${y1} A80,80 0 ${large},1 ${x2},${y2} Z`,
        color: STATUS_COLORS[r.status]||'#64748b', count:r.count, status:r.status, pct };
    });
    return (
      <div style={{display:'flex',alignItems:'center',gap:24,flexWrap:'wrap'}}>
        <svg width={200} height={200} viewBox="0 0 200 200">
          {slices.map((s,i)=>(
            <path key={i} d={s.path} fill={s.color} stroke={c.card} strokeWidth={2}/>
          ))}
          <circle cx={100} cy={100} r={45} fill={c.card}/>
          <text x={100} y={96} textAnchor="middle" fill={c.text} fontSize={20} fontWeight={800}>
            {total}
          </text>
          <text x={100} y={114} textAnchor="middle" fill={c.sub} fontSize={10}>
            {rtl?'סה״כ':'Total'}
          </text>
        </svg>
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {slices.map((s,i)=>(
            <div key={i} style={{display:'flex',alignItems:'center',gap:8}}>
              <div style={{width:14,height:14,borderRadius:3,background:s.color,flexShrink:0}}/>
              <span style={{fontSize:13,color:c.text,fontWeight:600}}>
                {(rtl?STATUS_HE:STATUS_EN)[s.status]||s.status}
              </span>
              <span style={{fontSize:13,color:s.color,fontWeight:800}}>{s.count}</span>
              <span style={{fontSize:11,color:c.sub}}>({Math.round(s.pct*100)}%)</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const BarChart = ({ data: d }) => {
    if (!d?.length) return null;
    const max = Math.max(...d.map(r=>parseInt(r.count)));
    const W=480, H=180, pad=40, barW=Math.min(40, (W-pad*2)/d.length*0.6);
    return (
      <svg width="100%" viewBox={`0 0 ${W} ${H+60}`} style={{overflow:'visible'}}>
        {[0,Math.round(max/2),max].map((v,i)=>(
          <g key={i}>
            <line x1={pad} y1={H-v/max*(H-pad)} x2={W-10} y2={H-v/max*(H-pad)}
              stroke={c.border} strokeWidth={1} strokeDasharray="4,4"/>
            <text x={pad-4} y={H-v/max*(H-pad)+4} textAnchor="end" fill={c.sub} fontSize={10}>{v}</text>
          </g>
        ))}
        {d.map((r,i)=>{
          const x = pad+(W-pad*2)/d.length*i+(W-pad*2)/d.length/2-barW/2;
          const h = parseInt(r.count)/max*(H-pad)||2;
          const y = H-h;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={h} rx={4}
                fill="#3b82f6" opacity={0.85}/>
              <text x={x+barW/2} y={y-6} textAnchor="middle" fill={c.text} fontSize={11} fontWeight={700}>
                {r.count}
              </text>
              <text x={x+barW/2} y={H+16} textAnchor="middle" fill={c.sub} fontSize={10}
                transform={`rotate(-30,${x+barW/2},${H+16})`}>
                {(r.site_name||'').slice(0,12)}
              </text>
            </g>
          );
        })}
        <line x1={pad} y1={H} x2={W-10} y2={H} stroke={c.border} strokeWidth={2}/>
      </svg>
    );
  };

  const LineChart = ({ data: d }) => {
    if (!d?.length) return null;
    const max = Math.max(...d.map(r=>parseInt(r.count)),1);
    const W=480, H=140, pad=40;
    const pts = d.map((r,i)=>({
      x: pad+(W-pad*2)/(d.length-1||1)*i,
      y: H-parseInt(r.count)/max*(H-pad),
      count:r.count, day:r.day
    }));
    const path = pts.map((p,i)=>`${i===0?'M':'L'}${p.x},${p.y}`).join(' ');
    const area = `M${pts[0].x},${H} `+pts.map(p=>`L${p.x},${p.y}`).join(' ')+` L${pts[pts.length-1].x},${H} Z`;
    return (
      <svg width="100%" viewBox={`0 0 ${W} ${H+30}`} style={{overflow:'visible'}}>
        <defs>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3"/>
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/>
          </linearGradient>
        </defs>
        <path d={area} fill="url(#lineGrad)"/>
        <path d={path} fill="none" stroke="#3b82f6" strokeWidth={2.5} strokeLinejoin="round"/>
        {pts.map((p,i)=>(
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={4} fill="#3b82f6" stroke={c.card} strokeWidth={2}/>
            <text x={p.x} y={p.y-10} textAnchor="middle" fill={c.text} fontSize={10} fontWeight={700}>{p.count}</text>
            {i%(Math.ceil(d.length/6))===0 &&
              <text x={p.x} y={H+20} textAnchor="middle" fill={c.sub} fontSize={9}>
                {new Date(p.day).toLocaleDateString(rtl?'he-IL':'en-US',{month:'short',day:'numeric'})}
              </text>}
          </g>
        ))}
        <line x1={pad} y1={H} x2={W-10} y2={H} stroke={c.border} strokeWidth={1}/>
      </svg>
    );
  };

  const handlePrint = () => {
    const style = `
      <style>
        * { box-sizing: border-box; }
        body { font-family: Arial, sans-serif; direction: ${rtl?'rtl':'ltr'}; background:#fff; color:#1e293b; margin:0; padding:20px; }
        .header { display:flex; align-items:center; gap:16px; margin-bottom:28px; padding-bottom:18px; border-bottom:3px solid #f97316; }
        .logo-wrap { width:56px; height:56px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .brand h1 { font-size:24px; font-weight:900; margin:0 0 2px 0; color:#1e293b; letter-spacing:1px; }
        .brand h2 { font-size:12px; color:#f97316; margin:0; font-weight:600; letter-spacing:2px; text-transform:uppercase; }
        .brand h3 { font-size:13px; color:#64748b; margin:4px 0 0 0; font-weight:400; }
        .kpi-row { display:flex; gap:12px; margin-bottom:20px; }
        .kpi { flex:1; border-radius:8px; padding:12px 16px; border:1px solid #e2e8f0; border-top:3px solid; }
        .kpi-num { font-size:26px; font-weight:800; }
        .kpi-lbl { font-size:11px; color:#64748b; margin-top:2px; font-weight:600; }
        .section-title { font-size:14px; font-weight:700; margin-bottom:10px; color:#1e293b; border-bottom:1px solid #e2e8f0; padding-bottom:6px; }
        table { width:100%; border-collapse:collapse; font-size:12px; margin-top:8px; }
        th { background:#f8fafc; padding:9px 10px; text-align:${rtl?'right':'left'}; font-weight:700; color:#475569; border-bottom:2px solid #e2e8f0; }
        td { padding:8px 10px; border-bottom:1px solid #f1f5f9; color:#1e293b; }
        tr:nth-child(even) td { background:#f8fafc; }
        .badge { display:inline-block; padding:2px 8px; border-radius:4px; font-size:11px; font-weight:700; color:#fff; }
        .footer { margin-top:28px; padding-top:12px; border-top:1px solid #e2e8f0; font-size:11px; color:#94a3b8; text-align:center; }
        @media print { body { padding:12px; } .no-print { display:none!important; } }
      </style>`;
    const printContent = printRef.current?.innerHTML || '';
    const w = window.open('','_blank');
    if (!w) { alert(rtl ? 'הדפדפן חסם את החלון הקופץ - יש לאשר חלונות קופצים לאתר הזה ולנסות שוב' : 'Popup blocked - please allow popups for this site and try again'); return; }
    w.document.write(`<html><head><title>דוח SEM SIM</title>${style}</head><body>
      <div class="header">
        <div class="logo-wrap"><img src="${window.location.origin}/assets/logo-semsim.jpg" style="width:56px;height:56px;object-fit:contain;border-radius:12px;"/></div>
        <div class="brand">
          <h1>SEM SIM</h1>
          <h2>CUTTING-EDGE WIRELESS</h2>
          <h3>${rtl?'דוח התראות':'Alerts Report'} | ${new Date().toLocaleDateString(rtl?'he-IL':'en-US', {year:'numeric',month:'long',day:'numeric'})}</h3>
        </div>
      </div>
      ${printContent}
      <div class="footer">SEM SIM © ${new Date().getFullYear()} | ${rtl?'הופק באמצעות מערכת SEM SIM':'Generated by SEM SIM System'}</div>
    </body></html>`);
    w.document.close();
    setTimeout(()=>{ w.print(); }, 600);
  };

  const statusLabel = s => (rtl ? STATUS_HE : STATUS_EN)[s] || s;
  const statusColor = s => STATUS_COLORS[s] || '#64748b';

  return (
    <div style={{display:'flex',flexDirection:'column',minHeight:'100vh',
      background:c.bg, color:c.text, fontFamily:'system-ui,Arial,sans-serif',
      direction:rtl?'rtl':'ltr'}}>
      <style>{`*{box-sizing:border-box} @media print{.no-print{display:none!important}}`}</style>

      {/* Topbar */}
      <div className="no-print" style={{height:54,background:c.topbar,
        borderBottom:`2px solid ${c.border}`,display:'flex',alignItems:'center',
        justifyContent:'space-between',padding:'0 20px',flexShrink:0,gap:10}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <div style={{width:36,height:36,background:'#f97316',borderRadius:8,
            display:'flex',alignItems:'center',justifyContent:'center',
            fontSize:13,fontWeight:800,color:'#fff',cursor:'pointer'}} onClick={() => window.location.href='/moked'}>SS</div>
          <div>
            <div style={{fontSize:15,fontWeight:800,color:'#fff',letterSpacing:1}}>SEM SIM</div>
            <div style={{fontSize:9,color:'#f97316',letterSpacing:1.5,fontWeight:700}}>REPORTS</div>
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <button onClick={()=>setLang(rtl?'en':'he')}
            style={{background:c.card,border:`1px solid ${c.border}`,color:c.text,
              borderRadius:6,padding:'5px 11px',fontSize:12,cursor:'pointer',fontWeight:700}}>
            {rtl?'EN':'עב'}
          </button>
          <button onClick={()=>setDark(!dark)}
            style={{background:c.card,border:`1px solid ${c.border}`,color:c.text,
              borderRadius:6,padding:'5px 11px',fontSize:14,cursor:'pointer'}}>
            {dark?'☀️':'🌙'}
          </button>
          <button onClick={handlePrint}
            style={{background:'#1d4ed8',border:'none',color:'#fff',
              borderRadius:6,padding:'7px 16px',fontSize:12,cursor:'pointer',fontWeight:700}}>
            📄 {rtl?'ייצוא PDF':'Export PDF'}
          </button>
          <button onClick={() => window.location.href='/moked'}
            style={{background:'transparent',border:`1px solid ${c.border}`,color:c.sub,
              borderRadius:6,padding:'5px 12px',fontSize:12,cursor:'pointer'}}>
            {rtl?'← חזרה':'← Back'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="no-print" style={{background:c.card,borderBottom:`2px solid ${c.border}`,
        padding:'12px 20px',display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>
        {[
          ['from', rtl?'מתאריך':'From', 'date'],
          ['to',   rtl?'עד תאריך':'To',   'date'],
        ].map(([k,label,type])=>(
          <div key={k} style={{display:'flex',flexDirection:'column',gap:4}}>
            <label style={{fontSize:11,color:c.sub,fontWeight:600}}>{label}</label>
            <input type={type} value={filters[k]} onChange={e=>setF(k,e.target.value)}
              style={{background:c.input,border:`1px solid ${c.border}`,color:c.text,
                borderRadius:6,padding:'6px 10px',fontSize:12}}/>
          </div>
        ))}
        <div style={{display:'flex',flexDirection:'column',gap:4}}>
          <label style={{fontSize:11,color:c.sub,fontWeight:600}}>{rtl?'לקוח':'Client'}</label>
          <select value={filters.municipality} onChange={e=>setF('municipality',e.target.value)}
            style={{background:c.input,border:`1px solid ${c.border}`,color:c.text,
              borderRadius:6,padding:'6px 10px',fontSize:12,minWidth:150}}>
            <option value="">{rtl?'כל הלקוחות':'All Clients'}</option>
            {munis.map(m=><option key={m.id||m.name} value={m.name}>{m.name}</option>)}
          </select>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:4}}>
          <label style={{fontSize:11,color:c.sub,fontWeight:600}}>{rtl?'סטטוס':'Status'}</label>
          <select value={filters.status} onChange={e=>setF('status',e.target.value)}
            style={{background:c.input,border:`1px solid ${c.border}`,color:c.text,
              borderRadius:6,padding:'6px 10px',fontSize:12,minWidth:130}}>
            <option value="">{rtl?'כל הסטטוסים':'All Statuses'}</option>
            {Object.entries(rtl?STATUS_HE:STATUS_EN).map(([k,v])=>(
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:4}}>
          <label style={{fontSize:11,color:c.sub,fontWeight:600}}>{rtl?'אתר':'Site'}</label>
          <select value={filters.site_id} onChange={e=>setF('site_id',e.target.value)}
            style={{background:c.input,border:`1px solid ${c.border}`,color:c.text,
              borderRadius:6,padding:'6px 10px',fontSize:12,minWidth:140}}>
            <option value="">{rtl?'כל האתרים':'All Sites'}</option>
            {sites.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <button onClick={()=>loadData(filters)}
          style={{background:'#1d4ed8',border:'none',color:'#fff',borderRadius:6,
            padding:'8px 20px',fontSize:13,cursor:'pointer',fontWeight:700,alignSelf:'flex-end'}}>
          {rtl?'🔍 הפק דוח':'🔍 Generate'}
        </button>
        <button onClick={()=>{ const f={from:'',to:'',municipality:'',site_id:'',status:''}; setFilters(f); loadData(f); }}
          style={{background:'transparent',border:`1px solid ${c.border}`,color:c.sub,
            borderRadius:6,padding:'8px 14px',fontSize:12,cursor:'pointer',alignSelf:'flex-end'}}>
          {rtl?'נקה':'Clear'}
        </button>
      </div>

      {/* Content */}
      <div style={{flex:1,padding:'20px',overflowY:'auto'}}>
        {loading && (
          <div style={{textAlign:'center',padding:60,color:c.sub,fontSize:16}}>
            ⏳ {t.loading}
          </div>
        )}

        {!loading && data && (
          <div ref={printRef}>

            {/* KPI cards */}
            <div style={{display:'flex',gap:14,marginBottom:20,flexWrap:'wrap'}}>
              {[
                { label:rtl?'סה״כ התראות':'Total Alerts', value: data.detail?.length||0, color:'#60a5fa' },
                { label:rtl?'חדשות':'New',        value: data.byStatus?.find(r=>r.status==='new')?.count||0,          color:'#ef4444' },
                { label:rtl?'בטיפול':'In Progress', value: (parseInt(data.byStatus?.find(r=>r.status==='acknowledged')?.count||0)+parseInt(data.byStatus?.find(r=>r.status==='in_treatment')?.count||0)), color:'#f59e0b' },
                { label:rtl?'סגורות':'Resolved',   value: data.byStatus?.find(r=>r.status==='resolved')?.count||0,    color:'#22c55e' },
              ].map(({label,value,color})=>(
                <div key={label} style={{flex:1,minWidth:120,background:c.card,
                  border:`1px solid ${c.border}`,borderRadius:10,padding:'16px',
                  borderTop:`3px solid ${color}`}}>
                  <div style={{fontSize:28,fontWeight:800,color}}>{value}</div>
                  <div style={{fontSize:12,color:c.sub,marginTop:4,fontWeight:600}}>{label}</div>
                </div>
              ))}
            </div>

            {/* Charts row */}
            <div style={{display:'flex',gap:16,marginBottom:20,flexWrap:'wrap'}}>

              {/* Pie */}
              <div style={{flex:'0 0 auto',background:c.card,border:`1px solid ${c.border}`,
                borderRadius:10,padding:'16px 20px',minWidth:300}}>
                <div style={{fontSize:13,fontWeight:700,color:c.sub,marginBottom:14,letterSpacing:0.5}}>
                  📊 {rtl?'פילוח לפי סטטוס':'By Status'}
                </div>
                <PieChart data={data.byStatus}/>
              </div>

              {/* Bar */}
              <div style={{flex:1,minWidth:300,background:c.card,border:`1px solid ${c.border}`,
                borderRadius:10,padding:'16px 20px'}}>
                <div style={{fontSize:13,fontWeight:700,color:c.sub,marginBottom:14,letterSpacing:0.5}}>
                  📈 {rtl?'התראות לפי אתר':'By Site'}
                </div>
                <BarChart data={data.bySite}/>
              </div>
            </div>

            {/* Line chart */}
            <div style={{background:c.card,border:`1px solid ${c.border}`,
              borderRadius:10,padding:'16px 20px',marginBottom:20}}>
              <div style={{fontSize:13,fontWeight:700,color:c.sub,marginBottom:14,letterSpacing:0.5}}>
                📉 {rtl?'התראות לפי יום':'Alerts Over Time'}
              </div>
              <LineChart data={data.byDay}/>
            </div>

            {/* Table */}
            <div style={{background:c.card,border:`1px solid ${c.border}`,borderRadius:10,overflow:'hidden'}}>
              <div style={{padding:'12px 16px',borderBottom:`1px solid ${c.border}`,
                fontSize:13,fontWeight:700,color:c.sub}}>
                📋 {rtl?'פירוט התראות':'Alert Details'} ({data.detail?.length||0})
              </div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:c.th}}>
                      {[
                        rtl?'אתר':'Site',
                        rtl?'עיר':'City',
                        rtl?'סטטוס':'Status',
                        rtl?'נפתח':'Triggered',
                        rtl?'נסגר':'Resolved',
                        rtl?'הערות':'Notes',
                      ].map(h=>(
                        <th key={h} style={{padding:'10px 12px',textAlign:rtl?'right':'left',
                          fontWeight:700,color:c.sub,fontSize:12,whiteSpace:'nowrap',
                          borderBottom:`1px solid ${c.border}`}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.detail?.map((row,i)=>(
                      <tr key={row.id} style={{background:i%2===0?c.tr1:c.tr2}}>
                        <td style={{padding:'9px 12px',color:c.text,fontWeight:600}}>{row.site_name}</td>
                        <td style={{padding:'9px 12px',color:c.sub}}>{row.site_city}</td>
                        <td style={{padding:'9px 12px'}}>
                          <span style={{background:statusColor(row.status),color:'#fff',
                            borderRadius:4,padding:'2px 8px',fontSize:11,fontWeight:700}}>
                            {statusLabel(row.status)}
                          </span>
                        </td>
                        <td style={{padding:'9px 12px',color:c.sub,whiteSpace:'nowrap'}}>
                          {new Date(row.triggered_at).toLocaleString(rtl?'he-IL':'en-US')}
                        </td>
                        <td style={{padding:'9px 12px',color:c.sub,whiteSpace:'nowrap'}}>
                          {row.resolved_at ? new Date(row.resolved_at).toLocaleString(rtl?'he-IL':'en-US') : '—'}
                        </td>
                        <td style={{padding:'9px 12px',color:c.sub,maxWidth:180,
                          overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {row.resolution_notes||'—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

